import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ExternalLink, Check, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { alpha } from "@/lib/color";

function TimerRing({ remaining, total, color }) {
  const r = 17;
  const c = 2 * Math.PI * r;
  const ratio = total > 0 ? Math.max(0, remaining / total) : 1;
  const urgent = remaining <= 5;
  return (
    <div
      className="relative h-11 w-11 shrink-0"
      data-testid="quiz-timer"
      data-remaining={remaining}
    >
      <svg viewBox="0 0 40 40" className="h-11 w-11 -rotate-90">
        <circle cx="20" cy="20" r={r} fill="none" stroke="currentColor" strokeWidth="3" className="text-border" />
        <circle
          cx="20" cy="20" r={r} fill="none" strokeWidth="3" strokeLinecap="round"
          stroke={urgent ? "#ef4444" : color}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - ratio)}
          style={{ transition: "stroke-dashoffset 0.9s linear, stroke 0.3s" }}
        />
      </svg>
      <span
        className={`absolute inset-0 flex items-center justify-center text-[12px] font-bold tabular-nums ${urgent ? "text-red-500 animate-pulse" : ""}`}
      >
        {remaining}
      </span>
    </div>
  );
}

/**
 * One quiz question: progress, optional countdown (paused while the tab is
 * hidden, e.g. when the player opens the help link), answers, next button.
 */
export default function QuizQuestion({
  question,
  index,
  total,
  selected,
  onChange,
  onNext,
  timePerQuestion,
  themeColor,
  themeBg,
  selectColor,
  btnText = "#fff",
}) {
  const { t } = useTranslation();
  const pick = selectColor || themeColor;
  const isLast = index === total - 1;
  const [remaining, setRemaining] = useState(timePerQuestion || 0);
  const firedRef = useRef(false);

  useEffect(() => {
    setRemaining(timePerQuestion || 0);
    firedRef.current = false;
  }, [index, timePerQuestion]);

  useEffect(() => {
    if (!timePerQuestion) return undefined;
    const id = setInterval(() => {
      if (document.hidden) return;
      setRemaining((r) => (r > 0 ? r - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [index, timePerQuestion]);

  useEffect(() => {
    if (!timePerQuestion || remaining > 0 || firedRef.current) return;
    firedRef.current = true;
    onNext();
  }, [remaining, timePerQuestion, onNext]);

  function toggle(i) {
    if (question.multiple) {
      onChange(selected.includes(i) ? selected.filter((x) => x !== i) : [...selected, i]);
    } else {
      onChange([i]);
    }
  }

  return (
    <motion.div
      key={index}
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      transition={{ duration: 0.3 }}
      className="qrise-card p-5 sm:p-7 space-y-5"
      data-testid={`quiz-question-${index}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            <span data-testid="quiz-progress">{t("play.quiz_progress", { current: index + 1, total })}</span>
            <span data-testid="quiz-answer-hint">
              {!question.multiple
                ? t("play.quiz_single_hint")
                : question.expected_count
                  ? t("play.quiz_expected_count", { count: question.expected_count })
                  : t("play.quiz_multiple_hint")}
            </span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: themeBg || themeColor }}
              initial={false}
              animate={{ width: `${((index + 1) / total) * 100}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>
        {timePerQuestion > 0 && <TimerRing remaining={remaining} total={timePerQuestion} color={themeColor} />}
      </div>

      <h3 className="text-lg sm:text-xl font-bold tracking-tight leading-snug" data-testid="quiz-question-text">
        {question.text}
      </h3>

      {question.link_url && (
        <a
          href={question.link_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-muted"
          style={{ borderColor: alpha(themeColor, 0.4), color: themeColor }}
          data-testid="quiz-question-link"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {question.link_label || t("play.quiz_link_default")}
        </a>
      )}

      <div className="space-y-2.5" role={question.multiple ? "group" : "radiogroup"}>
        {question.answers.map((a, i) => {
          const on = selected.includes(i);
          return (
            <button
              key={i}
              type="button"
              role={question.multiple ? "checkbox" : "radio"}
              aria-checked={on}
              onClick={() => toggle(i)}
              className="w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition-all active:scale-[0.99]"
              style={{
                borderColor: on ? pick : "hsl(var(--border))",
                background: on ? alpha(pick, 0.12) : "transparent",
              }}
              data-testid={`quiz-answer-${i}`}
              data-selected={on}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center border-2 transition-colors ${question.multiple ? "rounded-md" : "rounded-full"}`}
                style={{ borderColor: on ? pick : "hsl(var(--muted-foreground) / 0.4)", background: on ? pick : "transparent" }}
              >
                {on && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
              </span>
              <span className="flex-1">{a.text}</span>
            </button>
          );
        })}
      </div>

      <Button
        type="button"
        onClick={onNext}
        disabled={selected.length === 0}
        className="w-full h-12 rounded-xl text-sm font-bold uppercase tracking-wider hover:opacity-90"
        style={{ background: themeBg || themeColor, color: btnText }}
        data-testid="quiz-next-btn"
      >
        {isLast ? t("play.quiz_submit") : t("play.quiz_next")}
        <ArrowRight className="h-4 w-4 ml-1.5" />
      </Button>
      {timePerQuestion > 0 && (
        <p className="text-center text-[11px] text-muted-foreground inline-flex w-full items-center justify-center gap-1">
          <Clock className="h-3 w-3" />
          {t("play.quiz_timer_hint", { seconds: timePerQuestion })}
        </p>
      )}
    </motion.div>
  );
}
